import { type TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { RevealPackCardsDialogFromReward } from 'components/commonCard/RevealPackCardsDialogFromReward';

import type {
  useSeriesClaimDailyPackMutation,
  useSeriesClaimDailyPackMutationVariables,
  useSeriesDailyClaim_task,
} from './__generated__/useSeriesDailyClaim.graphql';

const SERIES_CLAIM_DAILY_PACK_MUTATION = gql`
  mutation useSeriesClaimDailyPackMutation($taskId: ID!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
      }
      rewards {
        id
        ...RevealPackCardsDialogFromReward_anyRewardInterface
      }
      errors {
        message
        code
      }
    }
  }
  ${RevealPackCardsDialogFromReward.fragments.anyRewardInterface}
` as TypedDocumentNode<
  useSeriesClaimDailyPackMutation,
  useSeriesClaimDailyPackMutationVariables
>;

export const useSeriesDailyClaim = (
  task?: useSeriesDailyClaim_task,
  onClaimed?: () => void
) => {
  const { isIosApp, isAndroidApp, postMessage } = useIsMobileApp();

  const {
    flags: { disableNativeIOSPackOpening = false },
  } = useFeatureFlags();
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [claimMutationData, setClaimMutationData] =
    useState<useSeriesClaimDailyPackMutation['claimTask']>();
  const [claimTask, { loading }] = useMutation(
    SERIES_CLAIM_DAILY_PACK_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );
  const isClaimable =
    task && task?.aasmState !== ManagerProgressionTaskState.CLAIMED;
  const pack = task?.rewardConfigs.find(
    isTypeFn('CardPacksRewardConfig')
  )?.cardPack;

  const onClaim = async () => {
    if (!isClaimable) {
      return;
    }
    if (isIosApp && !disableNativeIOSPackOpening) {
      // on ios the message opens the native pack opening dialog
      postMessage('claimFreeCardPack', {
        taskId: task.id,
      });
      return;
    }
    setPackDialogOpen(true);
    const { data } = await claimTask({
      variables: {
        taskId: task.id,
      },
    });
    if (isAndroidApp) {
      // on android the message refreshes the market red dot, pack opening is still handled by the web
      postMessage('claimFreeCardPack', {
        taskId: task.id,
      });
    }
    onClaimed?.();
    setClaimMutationData(data?.claimTask);
  };

  const reward = claimMutationData?.rewards?.at(0);

  return {
    loading,
    dailyTasksExpiresAt,
    packDialogOpen,
    setPackDialogOpen,
    claimMutationData,
    claimTask,
    isClaimable,
    pack,
    onClaim,
    reward,
  };
};
useSeriesDailyClaim.fragments = {
  task: gql`
    fragment useSeriesDailyClaim_task on Task {
      id
      aasmState
      rewardConfigs {
        id
        ... on CardPacksRewardConfig {
          id
          cardPack {
            slug
            pictureUrl
          }
        }
      }
    }
  ` as TypedDocumentNode<useSeriesDailyClaim_task>,
};
